import React, { useState, useEffect } from 'react';
import CountUp from 'react-countup';
import { TailSpin } from 'react-loader-spinner';
import { useAuth } from '../../../Layouts/context/Authcontext';
import api from '../../../../api';
import { IconTrendingUp, IconClock, IconCalendarEvent, IconCalendarCheck } from '@tabler/icons-react';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';
/**
 * RadialProgress: A circular progress indicator using a conic-gradient.
 * - No inner circle (so the ring is directly on the card background).
 * - Larger stroke width, bright white for progress, semi-transparent white for the track.
 * - Bigger text with a text shadow for clarity.
 */
function RadialProgress({ percentage, size = 80, strokeWidth = 10 }) {
  const [animatedValue, setAnimatedValue] = useState(0);

  useEffect(() => {
    let start = null;
    const duration = 1000; // animation duration in ms
    const animate = (timestamp) => {
      if (!start) start = timestamp;
      const progress = timestamp - start;
      const currentValue = Math.min(percentage, (progress / duration) * percentage);
      setAnimatedValue(currentValue);
      if (progress < duration) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [percentage]);

  // Define colors for the ring
  const progressColor = '#ffffff'; // bright white for the "filled" portion
  const trackColor = 'rgba(255, 255, 255, 0.3)'; // lighter track
  const angle = (animatedValue / 100) * 360;
  const background = `conic-gradient(${progressColor} ${angle}deg, ${trackColor} ${angle}deg 360deg)`;

  return (
    <div
      className="relative flex items-center justify-center rounded-full"
      style={{
        width: size,
        height: size,
        background,
      }}
    >
      {/* Centered percentage text */}
      <div
        className="absolute text-blue-500 font-bold"
        style={{
          fontSize: '1.25rem', // ~ text-xl
          textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
        }}
      >
        {Math.round(animatedValue)}
      </div>
    </div>
  );
}

function CardCompLeakage() {
  const [cardData, setCardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { accessToken } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        let baseUrls= getRoundRobinBaseUrl();
        const response = await api.get(`${baseUrls}/managerDashboard/getToolEffectiveness`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (response.data.success) {
          setCardData(response.data.data);
        } else {
          setError('Data fetch unsuccessful');
        }
      } catch (err) {
        // console.error('Error fetching data:', err);
        setError('Error fetching data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [accessToken]);

  if (loading) {
    return (
      <div className="flex justify-center items-center w-full h-full">
        <div className="flex flex-col justify-center items-center">
          <TailSpin
            height="80"
            width="80"
            color="#E5252E" // Brand red spinner
            ariaLabel="loading"
            className="animate-spin-slow"
          />
          <p className="text-[#19294F] ml-4 text-lg animate-pulse">Loading data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="text-[#E5252E] text-center font-semibold">{error}</div>;
  }

  if (!cardData) {
    return <div className="text-center text-[#707070]">No data available</div>;
  }

  // Prepare cards data based on your API response.
  const cards = [
    {
      title: 'Previous Day',
      icon: <IconClock size={24} />,
      hours: cardData.previousDay.totalLeakageHours,
      percentage: cardData.previousDay.totalLeakagePercentage,
      scopeCount: cardData.previousDay.scopeCount,
      employeeCount: cardData.previousDay.employeeCount,
      floorHours: cardData.previousDay.totalLoggedHours,
      floorPercentage: cardData.previousDay.floorPercentage,
      productiveHours: cardData.previousDay.totalProductiveHours,
      productivePercentage: cardData.previousDay.productivePercentage,
    },
    {
      title: 'Previous Week',
      icon: <IconTrendingUp size={24} />,
      hours: cardData.previousWeek.totalLeakageHours,
      percentage: cardData.previousWeek.totalLeakagePercentage,
      scopeCount: cardData.previousWeek.scopeCount,
      employeeCount: cardData.previousWeek.employeeCount,
      floorHours: cardData.previousWeek.totalLoggedHours,
      floorPercentage: cardData.previousWeek.floorPercentage,
      productiveHours: cardData.previousWeek.totalProductiveHours,
      productivePercentage: cardData.previousWeek.productivePercentage,
    },
    {
      title: 'Current Month',
      icon: <IconCalendarEvent size={24} />,
      hours: cardData.mtd.totalLeakageHours,
      percentage: cardData.mtd.totalLeakagePercentage,
      scopeCount: cardData.mtd.scopeCount,
      employeeCount: cardData.mtd.employeeCount,
      floorHours: cardData.mtd.totalLoggedHours,
      floorPercentage: cardData.mtd.floorPercentage,
      productiveHours: cardData.mtd.totalProductiveHours,
      productivePercentage: cardData.mtd.productivePercentage,
    },
    {
      title: 'Current Year',
      icon: <IconCalendarCheck size={24} />,
      hours: cardData.ytd.totalLeakageHours,
      percentage: cardData.ytd.totalLeakagePercentage,
      scopeCount: cardData.ytd.scopeCount,
      employeeCount: cardData.ytd.employeeCount,
      floorHours: cardData.ytd.totalLoggedHours,
      floorPercentage: cardData.ytd.floorPercentage,
      productiveHours: cardData.ytd.totalProductiveHours,
      productivePercentage: cardData.ytd.productivePercentage,
    },
  ];

  return (
    <>
      {/* Base styles for the flip-card effect + glow effect */}
      <style>{`
        .flip-card {
          perspective: 1000px;
        }
        .flip-card-inner {
          position: relative;
          width: 100%;
          height: 100%;
          transform-style: preserve-3d;
          transition: transform 0.8s;
        }
        .flip-card:hover .flip-card-inner {
          transform: rotateY(180deg);
        }
        .flip-card-front,
        .flip-card-back {
          position: absolute;
          width: 100%;
          height: 100%;
          border-radius: 1rem;
          backface-visibility: hidden;
          overflow: hidden;
        }
        .flip-card-back {
          transform: rotateY(180deg);
        }

        /* Glow effect around radial circles */
        .glow-circle {
          position: relative;
          display: inline-block; /* ensure it wraps tightly around the circle */
        }
        .glow-circle::before {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: 9999px;
          box-shadow: 0 0 10px 3px rgba(255, 255, 255, 0.5);
          opacity: 0.7;
          animation: pulseGlow 2s infinite;
        }
        @keyframes pulseGlow {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
      `}</style>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 w-full">
        {cards.map((card, index) => {
          // Convert the card's "percentage" field to a number in case it has "%"
          const numericLeakagePct = Number(String(card.percentage).replace('%', ''));
          const numericFloorPct = Number(String(card.floorHours || 0).replace('%', ''));
          const numericProdPct = Number(String(card.productiveHours || 0).replace('%', ''));

          return (
            <div
              key={index}
              className="flip-card cursor-pointer transition-transform hover:scale-105"
              style={{ height: '250px' }}
            >
              <div className="flip-card-inner">
                {/* Front Side (Navy) */}
                <div className="flip-card-front bg-[#19294F] text-white p-6 flex flex-col justify-between shadow-lg">
                  <div className="flex items-center mb-3">
                    {card.icon}
                    <h6 className="ml-2 text-xl font-bold uppercase tracking-wider">{card.title}</h6>
                  </div>
                  <div className="flex-grow flex items-center justify-center">
                    <div className="text-4xl font-extrabold">
                      <CountUp end={card.hours} duration={1.2} decimals={2} separator="," />
                      <span className="text-base block">Leakage hrs</span>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    {/* Display the leakage percentage in the radial progress, with glow */}
                    <div className="glow-circle">
                      <RadialProgress percentage={numericLeakagePct} size={70} strokeWidth={10} />
                    </div>
                  </div>
                </div>

                {/* Back Side (Red) */}
                <div className="flip-card-back bg-[#E5252E] text-white p-4 flex flex-col justify-between shadow-lg">
                  <div className="text-center font-bold text-lg mb-2">Floor &amp; Productive Hours</div>
                  <div className="flex justify-around mb-4">
                    <div className="flex flex-col items-center">
                      <div className="glow-circle">
                        <RadialProgress percentage={numericFloorPct} size={70} strokeWidth={10} />
                      </div>
                      <span className="mt-2 text-sm font-semibold">Floor hrs</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="glow-circle">
                        <RadialProgress percentage={numericProdPct} size={70} strokeWidth={10} />
                      </div>
                      <span className="mt-2 text-sm font-semibold">Productive hrs</span>
                    </div>
                  </div>
                  <div className="flex justify-around text-center">
                    <div>
                      <div className="text-xl font-bold">
                        <CountUp end={card.employeeCount} duration={1.2} separator="," />
                      </div>
                      <div className="text-sm">Employees</div>
                    </div>
                    <div>
                      <div className="text-xl font-bold">
                        <CountUp end={card.scopeCount} duration={1.2} separator="," />
                      </div>
                      <div className="text-sm">Scopes</div>
                    </div>
                  </div>
                </div>
                {/* End Back Side */}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

export default CardCompLeakage;
